import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const ZeroKnowledgePreferences = ({ preferences, onPreferencesChange }) => {
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const proofTypes = [
    { 
      value: 'zk-snark', 
      label: 'zk-SNARK',
      description: 'Zero-Knowledge Succinct Non-Interactive Argument of Knowledge - High privacy, fast verification'
    },
    { 
      value: 'zk-stark', 
      label: 'zk-STARK',
      description: 'Zero-Knowledge Scalable Transparent Argument of Knowledge - Quantum resistant, transparent'
    },
    { 
      value: 'bulletproofs', 
      label: 'Bulletproofs',
      description: 'Short non-interactive zero-knowledge proofs - Efficient for range proofs'
    }
  ];

  const privacyLevels = [
    { value: 'maximum', label: 'Maximum Privacy', description: 'Highest privacy protection, slower verification' },
    { value: 'balanced', label: 'Balanced', description: 'Good privacy with reasonable performance' },
    { value: 'performance', label: 'Performance Optimized', description: 'Faster verification, standard privacy' }
  ];

  const verificationModes = [
    { value: 'selective', label: 'Selective Disclosure', description: 'Reveal only requested attributes' },
    { value: 'predicate', label: 'Predicate Proofs', description: 'Prove conditions without revealing values' },
    { value: 'range', label: 'Range Proofs', description: 'Prove values within ranges without disclosure' }
  ];

  const handlePreferenceChange = (key, value) => {
    const updatedPreferences = {
      ...preferences,
      zeroKnowledge: {
        ...preferences?.zeroKnowledge,
        [key]: value
      }
    };
    onPreferencesChange(updatedPreferences);
  };

  const handleAdvancedSettingChange = (key, value) => {
    const updatedPreferences = {
      ...preferences,
      zeroKnowledge: {
        ...preferences?.zeroKnowledge,
        advanced: {
          ...preferences?.zeroKnowledge?.advanced,
          [key]: value
        }
      }
    };
    onPreferencesChange(updatedPreferences);
  };

  const getPrivacyLevelColor = (level) => {
    switch (level) {
      case 'maximum': return 'text-success';
      case 'balanced': return 'text-warning';
      case 'performance': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const getPrivacyLevelIcon = (level) => {
    switch (level) {
      case 'maximum': return 'ShieldCheck';
      case 'balanced': return 'Shield';
      case 'performance': return 'Zap';
      default: return 'Circle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Zero-Knowledge Proof Preferences</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure privacy-preserving verification modes and cryptographic proof systems
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Eye" size={20} className="text-primary" />
          <span className="text-sm font-medium text-primary">Privacy First</span>
        </div>
      </div>
      <div className="space-y-6">
        {/* Default Privacy Level */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Default Privacy Level</label>
          <Select
            options={privacyLevels}
            value={preferences?.zeroKnowledge?.privacyLevel || 'balanced'}
            onChange={(value) => handlePreferenceChange('privacyLevel', value)}
            placeholder="Select privacy level"
          />
          <div className="flex items-center space-x-2 text-sm">
            <Icon 
              name={getPrivacyLevelIcon(preferences?.zeroKnowledge?.privacyLevel || 'balanced')} 
              size={16} 
              className={getPrivacyLevelColor(preferences?.zeroKnowledge?.privacyLevel || 'balanced')} 
            />
            <span className="text-muted-foreground">
              {privacyLevels?.find(l => l?.value === (preferences?.zeroKnowledge?.privacyLevel || 'balanced'))?.description}
            </span>
          </div>
        </div>

        {/* Preferred Proof Type */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Preferred Proof System</label>
          <Select
            options={proofTypes}
            value={preferences?.zeroKnowledge?.proofType || 'zk-snark'}
            onChange={(value) => handlePreferenceChange('proofType', value)}
            placeholder="Select proof system"
          />
          <div className="text-sm text-muted-foreground">
            {proofTypes?.find(p => p?.value === (preferences?.zeroKnowledge?.proofType || 'zk-snark'))?.description}
          </div>
        </div>

        {/* Verification Modes */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Enabled Verification Modes</label>
          <div className="space-y-3">
            {verificationModes?.map((mode) => (
              <div key={mode?.value} className="flex items-start space-x-3 p-3 border border-border rounded-lg">
                <Checkbox
                  checked={preferences?.zeroKnowledge?.enabledModes?.includes(mode?.value) || false}
                  onChange={(e) => {
                    const currentModes = preferences?.zeroKnowledge?.enabledModes || [];
                    const updatedModes = e?.target?.checked
                      ? [...currentModes, mode?.value]
                      : currentModes?.filter(m => m !== mode?.value);
                    handlePreferenceChange('enabledModes', updatedModes);
                  }}
                />
                <div className="flex-1">
                  <div className="font-medium text-sm text-foreground">{mode?.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{mode?.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy Options */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Privacy Options</label>
          <div className="space-y-3">
            <Checkbox
              label="Always use zero-knowledge proofs by default"
              description="Automatically apply privacy-preserving verification for all credential sharing"
              checked={preferences?.zeroKnowledge?.alwaysUseZK || false}
              onChange={(e) => handlePreferenceChange('alwaysUseZK', e?.target?.checked)}
            />
            
            <Checkbox
              label="Minimize metadata disclosure"
              description="Reduce additional information shared during verification processes"
              checked={preferences?.zeroKnowledge?.minimizeMetadata || false}
              onChange={(e) => handlePreferenceChange('minimizeMetadata', e?.target?.checked)}
            />
            
            <Checkbox
              label="Enable proof caching"
              description="Cache generated proofs for faster subsequent verifications"
              checked={preferences?.zeroKnowledge?.enableCaching || false}
              onChange={(e) => handlePreferenceChange('enableCaching', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Advanced Settings Toggle */}
        <div className="border-t border-border pt-4">
          <Button
            variant="ghost"
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            iconName={showAdvancedSettings ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            className="w-full justify-between"
          >
            Advanced Cryptographic Settings
          </Button>
        </div>

        {/* Advanced Settings */}
        {showAdvancedSettings && (
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Proof Generation Timeout (seconds)</label>
              <Select
                options={[
                  { value: '30', label: '30 seconds' },
                  { value: '60', label: '1 minute' },
                  { value: '120', label: '2 minutes' },
                  { value: '300', label: '5 minutes' }
                ]}
                value={preferences?.zeroKnowledge?.advanced?.timeout || '60'}
                onChange={(value) => handleAdvancedSettingChange('timeout', value)}
              />
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Circuit Complexity</label>
              <Select
                options={[
                  { value: 'low', label: 'Low - Faster generation, larger proofs' },
                  { value: 'medium', label: 'Medium - Balanced approach' },
                  { value: 'high', label: 'High - Slower generation, smaller proofs' }
                ]}
                value={preferences?.zeroKnowledge?.advanced?.complexity || 'medium'}
                onChange={(value) => handleAdvancedSettingChange('complexity', value)}
              />
            </div>

            <Checkbox
              label="Enable trusted setup verification"
              description="Verify the integrity of cryptographic parameters before proof generation"
              checked={preferences?.zeroKnowledge?.advanced?.verifyTrustedSetup || false}
              onChange={(e) => handleAdvancedSettingChange('verifyTrustedSetup', e?.target?.checked)}
            />

            <Checkbox
              label="Use hardware acceleration"
              description="Leverage available hardware for faster cryptographic operations"
              checked={preferences?.zeroKnowledge?.advanced?.useHardwareAcceleration || false}
              onChange={(e) => handleAdvancedSettingChange('useHardwareAcceleration', e?.target?.checked)}
            />
          </div>
        )}
      </div>
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Lock" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium">Zero-Knowledge Privacy</p>
            <p className="text-muted-foreground mt-1">
              Zero-knowledge proofs allow you to prove the validity of your credentials without revealing 
              the actual data, ensuring maximum privacy during verification processes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZeroKnowledgePreferences;